import { QueryObserverResult } from "@tanstack/react-query";
import { Toast } from "primereact/toast";
import { RefObject, useEffect, useState, useRef } from "react";
import { Message } from "domain/entities/message";
import { Dialog } from "primereact/dialog";
import { Parameter } from "domain/entities/parameter";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { useParameterStore } from "presentation/stores/use-parameter-store";
import { useCreateExpenseMutation } from "presentation/hooks/use-query-expense";
import Spinner from "presentation/components/Shared/Spinner";
import MobileDropdownComponent from "presentation/mobile/components/MobileDropdownComponent";
import { translateText } from "infra/translation/translate";
import { MandatorySignal } from "presentation/components/Shared/MandatorySignalComponent";
import { Expense } from "domain/entities/expense";
import MobileInputCurrencyComponent from "presentation/mobile/components/MobileInputCurrencyComponent";
import MobileInputDateComponent from "presentation/mobile/components/MobileInputDateComponent";
import MobileInputTextAreaComponent from "presentation/mobile/components/MobileInputTextAreaComponent";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { httpClient } from "infra/axios/axios-api";
import MobilePDFViewer from "presentation/mobile/components/MobilePDFViewer";
import MobileDocumentUploader from "presentation/mobile/components/MobileDocumentUploader";
import MobileBottomSheet from "presentation/mobile/components/MobileBottomSheet";
import MobileToast from "presentation/mobile/components/MobileToast";

export interface RegisterExpenseProps {
    visible: boolean;
    setVisible: (isVisible: boolean) => void;
    toastRef?: RefObject<Toast | null>;
    refetch: () => Promise<QueryObserverResult<Message<Expense[]> | undefined, Error>>;
}

interface ExpenseParams {
    date: Date;
    value: number;
    type: Parameter;
    description: string;
    hasNimberNIF: boolean;
}

const RegisterExpensesModal = ({ visible, setVisible, toastRef, refetch }: RegisterExpenseProps) => {
    const { email: userEmployeeEmail, userEmployee } = useAuthStore();
    const { setExpenseType, expenseType } = useParameterStore();
    const localToastRef = useRef<Toast>(null);
    const activeToast = toastRef ?? localToastRef;
    const [isContaboPending, setIsContaboPending] = useState(false);
    const [doc, setDoc] = useState<{ file: File; name: string; url: string } | null>(null);
    const [expenseParameters, setExpenseParameters] = useState<ExpenseParams>();
    const [showDoc, setShowDoc] = useState(false);

    const { mutateAsync: createExpenseMutation, isPending: isCreateExpensePending } =
        useCreateExpenseMutation();

    const { data: parameterData } = useParameterByFilter({
        limit: 1000,
        sortBy: "value",
    } as BaseInputFilter);

    useEffect(() => {
        if (!parameterData?.data) return;
        setExpenseType(parameterData.data.filter((item) => item.type === "expenseType"));
    }, [parameterData?.data, setExpenseType]);

    const cleanData = () => {
        setExpenseParameters(undefined);
        setDoc(null);
    };

    const handleSave = async () => {
        if (!doc || !expenseParameters?.date || !expenseParameters?.type || !expenseParameters?.value) {
            activeToast.current?.show({
                severity: "warn",
                summary: "Aviso",
                detail: "Por favor, preencha todos os campos obrigatórios e anexe o comprovativo.",
            });
            return;
        }

        const month = new Date(expenseParameters.date).getMonth() + 1;
        const year = new Date(expenseParameters.date).getFullYear();
        const formData = new FormData();
        const extension = doc.name.split(".").pop();
        const safeDocName = `${month}_${year}_expense_${Date.now()}`;
        const fileNameWithExtension = `${safeDocName}.${extension}`;
        formData.append("file", doc.file, fileNameWithExtension);

        try {
            setIsContaboPending(true);

            const response = await httpClient.post(
                `/assets/expenses/${userEmployee?.id}`,
                formData
            );

            const expenseToCreate = {
                filePath: response.data.filePath,
                date: expenseParameters.date,
                value: expenseParameters.value,
                type: expenseParameters.type,
                employeeId: userEmployee?.id,
                lastModifiedBy: userEmployeeEmail ?? "",
                status: "Pendente",
                description: expenseParameters.description,
                hasNimberNIF: expenseParameters.hasNimberNIF || false,
            };

            await createExpenseMutation(expenseToCreate);
            activeToast.current?.show({
                severity: "success",
                summary: "Sucesso",
                detail: "Despesa registada com sucesso.",
            });
            cleanData();
            await refetch();
            setVisible(false);
        } catch (error: any) {
            console.error("Erro ao registar despesa:", error);
            const errorMessage =
                error?.response?.data?.metadata?.errors?.[0] ||
                error?.response?.data?.message ||
                error?.message ||
                "Ocorreu um erro ao registar a despesa. Tente novamente.";
            activeToast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: errorMessage,
            });
        } finally {
            setIsContaboPending(false);
        }
    };

    const footer = (
        <div className="w-full flex justify-content-center align-items-center gap-3">
            <Button
                label="Cancelar"
                type="button"
                className="btn-mobile-secondary"
                style={{ width: "135px", height: "42px" }}
                onClick={() => {
                    cleanData();
                    setVisible(false);
                }}
                disabled={isCreateExpensePending || isContaboPending}
            />
            <Button
                label={isCreateExpensePending || isContaboPending ? "A registar..." : "Registar"}
                icon={isCreateExpensePending || isContaboPending ? "pi pi-spin pi-spinner" : "pi pi-check"}
                type="button"
                className="btn-mobile"
                style={{ width: "135px", height: "42px" }}
                disabled={
                    isCreateExpensePending ||
                    isContaboPending ||
                    !expenseParameters?.date ||
                    !expenseParameters?.type ||
                    !expenseParameters?.value ||
                    !doc
                }
                onClick={handleSave}
            />
        </div>
    );

    return (
        <>
            {!toastRef && <MobileToast ref={localToastRef} />}


            <MobileBottomSheet
                visible={visible}
                onHide={() => {
                    cleanData();
                    setVisible(false);
                }}
                title="Registar Nova Despesa"
                headerIcon="pi pi-euro"
                footer={footer}
            >
                {isCreateExpensePending || isContaboPending ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: "260px" }}>
                        <Spinner />
                    </div>
                ) : (
                    <form className="w-full flex flex-column gap-3 py-1">
                        {/* Card 1: Dados da Despesa (Estilo Mapa de Horas) */}
                        <div
                            className="border-1 border-round-xl p-3 flex flex-column gap-3 bg-white"
                            style={{ borderColor: "#E2E8F0" }}
                        >
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-receipt" style={{ color: "#0084D1" }} />
                                <span className="text-sm font-semibold" style={{ color: "#1E293B" }}>
                                    Dados da Despesa
                                </span>
                            </div>

                            <div>
                                <label className="text-xs font-semibold mb-1 block" style={{ color: "#64748B" }}>
                                    {translateText("Tipo de Despesa", "pt")} {MandatorySignal()}
                                </label>
                                <MobileDropdownComponent
                                    options={expenseType}
                                    label={translateText("Tipo de Despesa", "pt")}
                                    fieldName="type"
                                    currentObject={expenseParameters}
                                    getValue={expenseParameters?.type}
                                    setValue={setExpenseParameters}
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold mb-1 block" style={{ color: "#64748B" }}>
                                    Valor {MandatorySignal()}
                                </label>
                                <MobileInputCurrencyComponent
                                    label="Valor"
                                    fieldName="value"
                                    currentObject={expenseParameters}
                                    getValue={expenseParameters?.value}
                                    setValue={setExpenseParameters}
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold mb-1 block" style={{ color: "#64748B" }}>
                                    Data da Despesa {MandatorySignal()}
                                </label>
                                <MobileInputDateComponent
                                    fieldName="date"
                                    dateFormat="dd/mm/yy"
                                    currentObject={expenseParameters}
                                    getValue={expenseParameters?.date}
                                    setValue={setExpenseParameters}
                                />
                            </div>

                            <div>
                                <label className="text-xs font-semibold mb-1 block" style={{ color: "#64748B" }}>
                                    Descrição
                                </label>
                                <MobileInputTextAreaComponent
                                    label={translateText("Description", "pt")}
                                    fieldName="description"
                                    currentObject={expenseParameters}
                                    getValue={expenseParameters?.description}
                                    setValue={setExpenseParameters}
                                />
                            </div>

                            <div className="flex align-items-center gap-2 pt-1">
                                <Checkbox
                                    inputId="hasNimberNIF"
                                    checked={expenseParameters?.hasNimberNIF || false}
                                    onChange={(e) =>
                                        setExpenseParameters((prev) => ({
                                            ...(prev as ExpenseParams),
                                            hasNimberNIF: e.checked || false,
                                        }))
                                    }
                                />
                                <label htmlFor="hasNimberNIF" className="text-xs font-medium cursor-pointer" style={{ color: "#475569" }}>
                                    A despesa tem o NIF da Nimber
                                </label>
                            </div>
                        </div>

                        {/* Card 2: Comprovativo (Estilo Mapa de Horas) */}
                        <div
                            className="border-1 border-round-xl p-3 flex flex-column gap-3 bg-white"
                            style={{ borderColor: "#E2E8F0" }}
                        >
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-paperclip" style={{ color: "#0084D1" }} />
                                <span className="text-sm font-semibold" style={{ color: "#1E293B" }}>
                                    Comprovativo
                                </span>
                            </div>

                            <MobileDocumentUploader
                                value={doc}
                                onChange={setDoc}
                                label="Comprovativo da Despesa *"
                                onView={() => setShowDoc(true)}
                            />
                        </div>
                    </form>
                )}

                {/* Visualizador do comprovativo anexado */}
                <Dialog
                    header="Visualização do Documento"
                    visible={showDoc}
                    style={{ width: "90vw", height: "85vh" }}
                    onHide={() => setShowDoc(false)}
                    maximized
                >
                    {doc &&
                        (doc.name.includes(".pdf") ? (
                            <MobilePDFViewer url={doc.url} fileName={doc.name} />
                        ) : (
                            <img src={doc.url} width="100%" alt="Comprovativo" height="auto" />
                        ))}
                </Dialog>
            </MobileBottomSheet>
        </>
    );
};

export default RegisterExpensesModal;